import React, { useState, useEffect } from 'react'
import { Plus, Search, IndianRupee, Layers, Hash } from 'lucide-react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import ExcelDataGrid from '../common/ExcelDataGrid'
import { useError } from '../../contexts/ErrorContext'

const TdsDetailsTable = ({ tdsData: propTdsData, challanData: propChallanData, onTdsDataChange, selectedFinancialYear, selectedQuarter, isRevised, isCorrection, originalData, sectionOptions: selectedSectionOptions, is27Q, remittanceOptions, countryOptions, applicableTdsOptions, is27EQ, collecteeOptions, hideRateColumn, is26Q, is24Q, is24Q4, formType }) => {
  const [tdsData, setTdsData] = useState(propTdsData || [])
  const [challanData, setChallanData] = useState(propChallanData || [])
  const [searchQuery, setSearchQuery] = useState('')
  const { addError } = useError()

  // Use refs to track latest props to avoid stale closures in Grid callbacks
  const fyRef = React.useRef(selectedFinancialYear);
  const quarterRef = React.useRef(selectedQuarter);

  useEffect(() => {
    fyRef.current = selectedFinancialYear;
  }, [selectedFinancialYear]);

  useEffect(() => {
    quarterRef.current = selectedQuarter;
  }, [selectedQuarter]);

  // Sync prop data with local state
  useEffect(() => {
    if (propTdsData) {
      const sanitized = propTdsData.map(row => {
        let tds = row.tdsAmount ?? row.tds ?? row.tdsAmt ?? row.taxAmount ?? row.totalTds ?? row.tcsAmount ?? row.tcsAmt;
        if (tds === undefined || tds === null || tds === '') {
          tds = 0;
        }
        return {
          ...row,
          tdsAmount: tds
        };
      });
      setTdsData(sanitized);
    }
  }, [propTdsData])

  useEffect(() => {
    if (propChallanData && propChallanData.length > 0) {
      setChallanData(propChallanData)
    }
  }, [propChallanData])

  // Default Section Options for 26Q if not provided
  const defaultSectionOptions = [
    { value: '192A', label: '192A - Payment of accumulated balance to employee (RPF)' },
    { value: '193', label: '193 - Interest on Securities' },
    { value: '194', label: '194 - Dividends' },
    { value: '194A', label: '194A - Interest other than "Interest on securities"' },
    { value: '194B', label: '194B - Winnings from lottery or crossword puzzle' },
    { value: '194B-P', label: '194B-P - Winnings from lottery (Kind/Cash)' },
    { value: '194BA', label: '194BA - Winnings from Online Games' },
    { value: '194BA-P', label: '194BA-P - Net Winnings from Online Games (Kind/Cash)' },
    { value: '194BB', label: '194BB - Winnings from Horse Race' },
    { value: '194C', label: '194C - Payments to Contractors/Sub-contractors' },
    { value: '194D', label: '194D - Insurance Commission' },
    { value: '194DA', label: '194DA - Payment in respect of Life Insurance Policy' },
    { value: '194EE', label: '194EE - Payments in respect of Deposit under NSS' },
    { value: '194F', label: '194F - Repurchase of units by Mutual Fund or UTI' },
    { value: '194G', label: '194G - Commission on sale of lottery tickets' },
    { value: '194H', label: '194H - Commission or brokerage' },
    { value: '194I', label: '194I - Rent (Applicable upto 2012-13)' },
    { value: '194I(a)', label: '194I(a) - Rent on Plant & Machinery' },
    { value: '194I(b)', label: '194I(b) - Rent on Land/Building/Furniture' },
    { value: '194IA', label: '194IA - TDS on sale of Property' },
    { value: '194IC', label: '194IC - Payment under Specified Agreement' },
    { value: '194J(a)', label: '194J(a) - Fees for Technical Services (@2%)' },
    { value: '194J(b)', label: '194J(b) - Fees for Professional Services (@10%)' },
    { value: '194K', label: '194K - Income from Mutual Fund Units' },
    { value: '194LA', label: '194LA - Payment of Compensation on acquisition of certain Immovable Property' },
    { value: '194LBA', label: '194LBA - Income from units of Business Trust' },
    { value: '194LBA(a)', label: '194LBA(a) - Interest from Business Trust to Resident' },
    { value: '194LBA(b)', label: '194LBA(b) - Dividend from Business Trust to Resident' },
    { value: '194LBB', label: '194LBB - Income in respect of units of Investment Fund' },
    { value: '194LBC', label: '194LBC - Income in respect of investment in Securitization Trust' },
    { value: '194N', label: '194N - Cash Withdrawal > 1 Crore' },
    { value: '194N-FT', label: '194N-FT - Cash payment to Non-filer Co-op Societies' },
    { value: '194NC', label: '194NC - Cash payment to Co-op Societies' },
    { value: '194NF', label: '194NF - Cash payment to non-filers' },
    { value: '194O', label: '194O - TDS on E-commerce transactions' },
    { value: '194Q', label: '194Q - Purchase of Goods' },
    { value: '194R', label: '194R - Benefits or Perquisites of Business/Profession' },
    { value: '194RP', label: '194RP - Benefits or Perquisites (Kind/Cash)' },
    { value: '194S', label: '194S - Transfer of Virtual Digital Asset' },
    { value: '194SP', label: '194SP - Transfer of Virtual Digital Asset (Kind/Exchange)' }
  ]

  const sectionOptions26Q_new = [
    { value: '1004', label: '1004 - Premature EPF Withdrawal' },
    { value: '1005', label: '1005 - Insurance Commission' },
    { value: '1006', label: '1006 - Commission or Brokerage (Others)' },
    { value: '1007', label: '1007 - Rent on Land/Building (By Individuals/HUF)' },
    { value: '1009', label: '1009 - Rent on Land/Building (Audit-liable cases)' },
    { value: '1010', label: '1010 - Rent on Plant, Machinery, or Equipment' },
    { value: '1011', label: '1011 - Life Insurance Policy Maturity Payments' },
    { value: '1012', label: '1012 - Compensation on Acquisition of Immovable Property' },
    { value: '1019', label: '1019 - Interest on Securities' },
    { value: '1021', label: '1021 - Interest other than Securities (Banks/Firms)' },
    { value: '1023', label: '1023 - Payment to Contractors (Carrying out any work)' },
    { value: '1024', label: '1024 - Supply of Labour/Manpower Contractors' },
    { value: '1026', label: '1026 - Fees for Technical Services (FTS) / Film Royalty' },
    { value: '1027', label: '1027 - Professional Fees (CAs, Lawyers, Doctors, etc.)' },
    { value: '1028', label: '1028 - Director Remuneration (Non-salary cases)' },
    { value: '1029', label: '1029 - Dividend Income' },
    { value: '1033', label: '1033 - TDS on Purchase of Goods (>50 Lakhs limit)' },
    { value: '1034', label: '1034 - TDS on E-commerce Participants' },
    { value: '1035', label: '1035 - Purchase of Immovable Property' },
    { value: '1037', label: '1037 - Transfer of Virtual Digital Assets (VDA / Crypto)' },
    { value: '1058', label: '1058 - Winnings from Lottery, Crosswords, or Card Games' },
    { value: '1060', label: '1060 - Winnings from Online Games' },
    { value: '1064', label: '1064 - Cash Withdrawals exceeding statutory limits' },
    { value: '1067', label: '1067 - Salary, Interest, or Bonus Paid to Partners of a Firm' }
  ];

  const sectionOptions = (is26Q && selectedFinancialYear && parseInt(selectedFinancialYear.split('-')[0]) >= 2026)
    ? sectionOptions26Q_new
    : (selectedSectionOptions || defaultSectionOptions);





  // Helper Functions for Revised/Correction Return Logic
  const isCorrectionMode = isRevised || isCorrection;

  const normalizeCode = (val) => {
    if (!val) return '';
    const str = String(val).trim();
    const parts = str.split('-');
    return parts[0].trim().toUpperCase();
  };

  const normalizeDate = (val) => {
    if (!val) return '';
    let str = String(val).trim();
    if (str.includes('-')) {
      const parts = str.split('T')[0].split('-');
      if (parts[0].length === 4) {
        // YYYY-MM-DD -> DDMMYYYY
        return `${parts[2]}${parts[1]}${parts[0]}`;
      } else if (parts[2].length === 4) {
        // DD-MM-YYYY -> DDMMYYYY
        return `${parts[0]}${parts[1]}${parts[2]}`;
      }
    }
    return str.replace(/[^0-9]/g, '');
  };

  const normalizeNumber = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const num = parseFloat(val);
    return isNaN(num) ? 0 : Math.round(num * 100) / 100;
  };

  const isRateEqual = (r1, r2) => {
    const num1 = normalizeNumber(r1);
    const num2 = normalizeNumber(r2);
    if (Math.abs(num1 - num2) < 0.01) return true;
    if (Math.abs(num1 - num2 * 100) < 0.01) return true;
    if (Math.abs(num1 * 100 - num2) < 0.01) return true;
    return false;
  };

  const normalizeStr = (val) => {
    if (val === null || val === undefined) return '';
    return String(val).trim().toUpperCase();
  };

  const getOriginalRow = (row, rowIndex) => {
    if (!isCorrectionMode || !originalData || originalData.length === 0 || !row) return null;

    // 1. Match exact unique ID across originalData
    if (row.id) {
      const matchId = originalData.find(r => r.id && String(r.id) === String(row.id));
      if (matchId) return matchId;
    }

    // 2. Fallback: Match PAN + Amount + Payment Date
    const matchFallback = originalData.find(r => 
      normalizeStr(r.panOfDeductee || r.pan) === normalizeStr(row.panOfDeductee || row.pan) && 
      Math.abs(normalizeNumber(r.amountOfPayment || r.amountPaid) - normalizeNumber(row.amountOfPayment || row.amountPaid)) < 0.01 &&
      normalizeDate(r.dateOfPayment || r.paymentDate) === normalizeDate(row.dateOfPayment || row.paymentDate)
    );
    if (matchFallback) return matchFallback;

    // 3. Match array index only if valid and IDs don't conflict
    if (rowIndex !== undefined && rowIndex >= 0 && rowIndex < originalData.length) {
      const candidate = originalData[rowIndex];
      if (candidate && (!row.id || !candidate.id || String(row.id) === String(candidate.id))) {
        return candidate;
      }
    }

    return null;
  };

  const isRowNew = (row, rowIndex) => {
    if (!isCorrectionMode) return false;
    if (!row || row.source === 'conso') return false;
    if (!originalData || originalData.length === 0) return false;
    return !getOriginalRow(row, rowIndex);
  };

  const isRowModified = (row, rowIndex) => {
    const original = getOriginalRow(row, rowIndex);
    if (!original) return false;

    // 1. Check Section Code
    const secRow = normalizeCode(row.sectionCode || row.section);
    const secOrig = normalizeCode(original.sectionCode || original.section);
    if (secRow && secOrig && secRow !== secOrig) {
      if (!secRow.includes(secOrig) && !secOrig.includes(secRow)) return true;
    }

    // 2. Check PAN
    const panRow = normalizeStr(row.panOfDeductee || row.pan);
    const panOrig = normalizeStr(original.panOfDeductee || original.pan);
    if (panRow && panOrig && panRow !== panOrig) return true;

    // 3. Check Payment Amount
    const amtRow = normalizeNumber(row.amountOfPayment || row.amountPaid);
    const amtOrig = normalizeNumber(original.amountOfPayment || original.amountPaid);
    if (Math.abs(amtRow - amtOrig) > 0.01) return true;

    // 4. Check TDS Amount
    const tdsRow = normalizeNumber(row.tdsAmount || row.tds || row.taxAmount);
    const tdsOrig = normalizeNumber(original.tdsAmount || original.tds || original.taxAmount);
    if (Math.abs(tdsRow - tdsOrig) > 0.01) return true;

    // 5. Check Rate of Tax
    if (!isRateEqual(row.rateOfTax || row.rate, original.rateOfTax || original.rate)) return true;

    // 6. Check Payment Date
    const payDateRow = normalizeDate(row.dateOfPayment || row.paymentDate);
    const payDateOrig = normalizeDate(original.dateOfPayment || original.paymentDate);
    if (payDateRow && payDateOrig && payDateRow !== payDateOrig) return true;

    // 7. Check Deduction Date
    const dedDateRow = normalizeDate(row.dateOfDeduction || row.deductionDate);
    const dedDateOrig = normalizeDate(original.dateOfDeduction || original.deductionDate);
    if (dedDateRow && dedDateOrig && dedDateRow !== dedDateOrig) return true;

    // 8. Check Certificate 197
    const certRow = normalizeStr(row.certificateNo197);
    const certOrig = normalizeStr(original.certificateNo197);
    if (certRow !== certOrig && certRow !== secRow && certOrig !== secOrig) return true;

    // 9. Check Reason for Non Deduction
    const reasonRow = normalizeStr(row.reasonForNonDeduction);
    const reasonOrig = normalizeStr(original.reasonForNonDeduction);
    if (reasonRow !== reasonOrig && reasonRow !== secRow && reasonOrig !== secOrig) return true;

    // 10. Check Nature of Remittance / Country / Deductee Code (for 27Q / 144)
    const remRow = normalizeCode(row.natureOfRemittance || row.remittanceCode);
    const remOrig = normalizeCode(original.natureOfRemittance || original.remittanceCode);
    if (remRow && remOrig && remRow.padStart(2, '0') !== remOrig.padStart(2, '0')) return true;

    const countryRow = normalizeCode(row.country || row.countryCode);
    const countryOrig = normalizeCode(original.country || original.countryCode);
    if (countryRow && countryOrig && countryRow.replace(/^0+/, '') !== countryOrig.replace(/^0+/, '')) return true;

    const dedCodeRow = normalizeCode(row.deducteeCode || row.status);
    const dedCodeOrig = normalizeCode(original.deducteeCode || original.status);
    if (dedCodeRow && dedCodeOrig && dedCodeRow.padStart(2, '0') !== dedCodeOrig.padStart(2, '0')) return true;

    return false;
  };

  const getRowClassName = (row, rowIndex) => {
    if (!isCorrectionMode) return '';
    if (isRowNew(row, rowIndex)) return 'bg-blue-100 text-blue-900 font-medium';
    if (isRowModified(row, rowIndex)) return 'bg-amber-100 text-amber-900 font-medium';
    return '';
  };

  const isRowDeleteDisabled = (row) => {
    // Allow delete only if it is NEW
    return isRevised && !isRowNew(row)
  }

  const handleRowRevert = (row) => {
    const original = getOriginalRow(row)
    if (original) {
      if (window.confirm('Revert changes to this row?')) {
        const updatedData = tdsData.map(r => r.id === row.id ? JSON.parse(JSON.stringify(original)) : r)
        setTdsData(updatedData)
        onTdsDataChange(updatedData)
      }
    }
  }

  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'changed'

  const changedCount = React.useMemo(() => {
    if (!isCorrectionMode || !tdsData) return 0;
    return tdsData.filter((row, idx) => isRowModified(row, idx) || isRowNew(row, idx)).length;
  }, [tdsData, isCorrectionMode, originalData]);

  const filteredTdsData = React.useMemo(() => {
    let result = tdsData;

    if (isCorrectionMode && filterMode === 'changed') {
      result = result.filter((row, idx) => isRowModified(row, idx) || isRowNew(row, idx));
    }

    if (!searchQuery) return result;
    const query = searchQuery.toLowerCase();
    return result.filter(row => {
      const pan = (row.panOfDeductee || '').toLowerCase();
      const firstName = (row.firstName || '').toLowerCase();
      const middleName = (row.middleName || '').toLowerCase();
      const lastName = (row.lastName || '').toLowerCase();
      const nameOfDeductee = (row.nameOfDeductee || '').toLowerCase();
      const deducteeName = (row.deducteeName || '').toLowerCase();

      const fullName = `${firstName} ${middleName} ${lastName} ${nameOfDeductee} ${deducteeName}`.trim();
      return pan.includes(query) || fullName.includes(query);
    });
  }, [searchQuery, tdsData, filterMode, isCorrectionMode]);

  // Grid Handlers
  const handleGridDataChange = (newData, rowIndex, columnKey) => {
    let fullData = [...tdsData];
    let targetIndex = rowIndex;

    if (searchQuery && rowIndex !== undefined) {
      const changedRow = newData[rowIndex];
      targetIndex = fullData.findIndex(r => r.id === changedRow.id);
      if (targetIndex !== -1) {
        fullData[targetIndex] = changedRow;
      }
    } else {
      fullData = newData;
    }

    // Validate Dates within Quarter
    if ((columnKey === 'dateOfPayment' || columnKey === 'dateOfDeduction') && targetIndex !== undefined && targetIndex !== -1) {
      const dateStr = fullData[targetIndex][columnKey]
      const currentFy = fyRef.current;
      const currentQuarter = quarterRef.current;

      if (dateStr && currentFy && currentQuarter) {
        // Robustly parse FY start year handling potential spaces
        const cleanFy = currentFy.replace(/\s/g, '')
        const fyStart = parseInt(cleanFy.split('-')[0])
        let minDate, maxDate

        if (currentQuarter === 'Q1') {
          minDate = `${fyStart}-04-01`
          maxDate = `${fyStart}-06-30`
        } else if (currentQuarter === 'Q2') {
          minDate = `${fyStart}-07-01`
          maxDate = `${fyStart}-09-30`
        } else if (currentQuarter === 'Q3') {
          minDate = `${fyStart}-10-01`
          maxDate = `${fyStart}-12-31`
        } else if (currentQuarter === 'Q4') {
          minDate = `${fyStart + 1}-01-01`
          maxDate = `${fyStart + 1}-03-31`
        }

        if (dateStr < minDate || dateStr > maxDate) {
          addError(`Date must be within ${currentQuarter} (${minDate} to ${maxDate})`)
          fullData[targetIndex][columnKey] = ''
          setTdsData([...fullData])
          onTdsDataChange([...fullData])
          return
        }
      }
    }

    // Auto-populate Reason for Non-Deduction based on PAN
    if (columnKey === 'panOfDeductee' && targetIndex !== undefined && targetIndex !== -1) {
      const pan = fullData[targetIndex].panOfDeductee?.toUpperCase() || '';
      if (['PANAPPLIED', 'PANINVALID', 'PANNOTAVBL'].includes(pan)) {
        fullData[targetIndex].reasonForNonDeduction = 'C';
      }
    }

    // Auto-calculate TDS Amount when Payment Amount or Rate % is entered/changed
    if ((columnKey === 'amountOfPayment' || columnKey === 'rateOfTax') && targetIndex !== undefined && targetIndex !== -1) {
      const row = fullData[targetIndex];
      const amt = parseFloat(row.amountOfPayment || 0);
      const rate = parseFloat(row.rateOfTax || 0);
      if (amt > 0 && rate > 0) {
        row.tdsAmount = Math.round(((amt * rate) / 100) * 100) / 100;
      }
    }

    // Sync remittanceCode / countryCode / status aliases for 27Q & 144
    if (columnKey === 'natureOfRemittance' && targetIndex !== undefined && targetIndex !== -1) {
      fullData[targetIndex].remittanceCode = fullData[targetIndex].natureOfRemittance;
    }
    if (columnKey === 'country' && targetIndex !== undefined && targetIndex !== -1) {
      fullData[targetIndex].countryCode = fullData[targetIndex].country;
    }
    if (columnKey === 'deducteeCode' && targetIndex !== undefined && targetIndex !== -1) {
      fullData[targetIndex].status = fullData[targetIndex].deducteeCode;
    }
    if (columnKey === 'sectionCode' && targetIndex !== undefined && targetIndex !== -1) {
      const row = fullData[targetIndex];
      const sec = String(row.sectionCode || '').trim();
      if (!row.natureOfRemittance) {
        if (['1040', '1041', '1042', '1043', '1044', '1045'].includes(sec)) {
          row.natureOfRemittance = '27';
          row.remittanceCode = '27';
        } else if (sec === '1052') {
          row.natureOfRemittance = '71';
          row.remittanceCode = '71';
        } else if (sec === '1054') {
          row.natureOfRemittance = '72';
          row.remittanceCode = '72';
        }
      }
    }

    // Helper to construct full name for comparison
    const getFullName = (r) => {
      if (r.deducteeName) return r.deducteeName.trim().toUpperCase();
      return [r.firstName, r.middleName, r.lastName].filter(Boolean).map(x => x.trim().toUpperCase()).join(' ');
    }

    // 1. Handle PAN Change or Name Change -> Auto-fill Ref No
    if (['panOfDeductee', 'firstName', 'middleName', 'lastName', 'deducteeName'].includes(columnKey) && targetIndex !== undefined && targetIndex !== -1) {
      const row = fullData[targetIndex];
      const pan = row.panOfDeductee?.toUpperCase() || '';
      const isInvalidPan = ['PANAPPLIED', 'PANINVALID', 'PANNOTAVBL'].includes(pan);

      if (isInvalidPan) {
        const fullName = getFullName(row);

        // Search for existing Ref with same Name + PAN
        const existingMatch = fullData.find((r, idx) =>
          idx !== targetIndex &&
          r.panOfDeductee?.toUpperCase() === pan &&
          getFullName(r) === fullName &&
          r.deducteeReferenceNo
        );

        if (existingMatch) {
          row.deducteeReferenceNo = existingMatch.deducteeReferenceNo;
        } else {
          const maxRef = fullData.reduce((max, r) => {
            const val = parseInt(r.deducteeReferenceNo);
            return !isNaN(val) && val > max ? val : max;
          }, 0);
          row.deducteeReferenceNo = String(maxRef + 1);
        }
      } else {
        // Valid PAN -> Clear Ref
        row.deducteeReferenceNo = '';
      }
    }

    // 2. Handle Ref No Change -> Sync others with same Name + PAN
    if (columnKey === 'deducteeReferenceNo' && targetIndex !== undefined && targetIndex !== -1) {
      const row = fullData[targetIndex];
      const pan = row.panOfDeductee?.toUpperCase() || '';
      const fullName = getFullName(row);

      // Only sync if it's an invalid PAN case
      if (['PANAPPLIED', 'PANINVALID', 'PANNOTAVBL'].includes(pan)) {
        fullData.forEach((r, idx) => {
          if (idx !== targetIndex &&
            r.panOfDeductee?.toUpperCase() === pan &&
            getFullName(r) === fullName) {
            r.deducteeReferenceNo = row.deducteeReferenceNo;
          }
        });
      }
    }

    setTdsData(fullData)
    onTdsDataChange(fullData)
  }

  const handleGridRowAdd = () => {
    const newRecord = {
      id: Date.now(),
      deducteeCode: '',
      panOfDeductee: '',
      firstName: '',
      middleName: '',
      lastName: '',
      amountOfPayment: '',
      dateOfPayment: '',
      sectionCode: '',
      rateOfTax: '',
      tdsAmount: '',
      dateOfDeduction: '',
      challanId: challanData && challanData.length === 1 ? challanData[0].id : '',
      dateOfFurnishingCertificate: '',
      reasonForNonDeduction: '',
      paidByBookEntry: false,
      certificateNo197: '',
      deducteeReferenceNo: '',
      recordNo: '',
      correction: false,
      // 27Q Fields
      natureOfRemittance: '',
      country: '',
      applicableTds: '',
      email: '',
      contactNo: '',
      address: '',
      tin: '',
      // 27EQ Fields
      residentialStatus: '',
      hasPE: '',
      optOut115BAC: ''
    }
    const updatedData = [...tdsData, newRecord]
    setTdsData(updatedData)
    onTdsDataChange(updatedData)
  }

  const handleGridRowDelete = (id) => {
    const row = tdsData.find(r => r.id === id)
    if (row && isRowDeleteDisabled(row)) {
      addError('Cannot delete original records in Revised Return')
      return
    }

    if (window.confirm('Are you sure you want to delete this row?')) {
      const updatedData = tdsData.filter(row => row.id !== id)
      setTdsData(updatedData)
      onTdsDataChange(updatedData)
    }
  }

  const getColumns = () => {
    const shouldHideRate = hideRateColumn || formType === '138' || is24Q;
    let currentFormType = '26Q';
    if (is27EQ || formType === '143') currentFormType = '27EQ';
    else if (is27Q || formType === '144') currentFormType = '27Q';
    else if (is24Q4) currentFormType = '24Q4';
    else if (shouldHideRate || formType === '138' || is24Q) currentFormType = '24Q';

    const reasonOptions = {
      '26Q': ['A', 'B', 'C', 'T', 'Y', 'S', 'Z', 'R', 'N', 'D', 'O', 'M', 'E', 'P', 'Q', 'U'],
      '24Q': ['A', 'B', 'C'],
      '24Q4': ['A', 'B', 'C'],
      '27Q': ['A', 'B', 'C', 'S', 'N', 'O', 'M', 'G', 'I', 'H', 'J', 'Y', 'P'],
      '27EQ': ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'T', 'MA', 'MB', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MI', 'MJ']
    };

    const currentReasonOptions = reasonOptions[currentFormType] || [];
    const formattedReasonOptions = currentReasonOptions.map(opt => ({ value: opt, label: opt }));

    const cols = []

    if (is27EQ || is27Q || is26Q) {
      cols.push({
        key: 'deducteeCode',
        label: is27EQ ? 'Collectee Code' : 'Deductee Code',
        type: 'select',
        options: collecteeOptions || [],
        width: '200px',
        required: true
      })
    }

    cols.push(
      { key: 'panOfDeductee', label: 'PAN', type: 'text', width: is27Q ? '160px' : '120px', required: true },
      {
        key: 'deducteeReferenceNo',
        label: 'Ref No.',
        type: 'text',
        width: '80px',
        readOnly: (row) => !['PANAPPLIED', 'PANINVALID', 'PANNOTAVBL'].includes(row.panOfDeductee?.toUpperCase()),
        disabled: (row) => !['PANAPPLIED', 'PANINVALID', 'PANNOTAVBL'].includes(row.panOfDeductee?.toUpperCase()),
        placeholder: 'Ref'
      },
      { key: 'firstName', label: 'First Name', type: 'text', width: '120px' },
      { key: 'middleName', label: 'Middle Name', type: 'text', width: '100px' },
      { key: 'lastName', label: 'Last Name', type: 'text', width: '120px' },
      {
        key: 'amountOfPayment',
        label: is27EQ ? 'Collected Amt' : 'Payment Amt',
        type: 'number',
        width: '120px',
        required: true
      },
      {
        key: 'dateOfPayment',
        label: is27EQ ? 'Date of Collection' : 'Date Payment',
        type: 'date',
        width: '120px',
        placeholder: 'DD-MM-YYYY',
        required: true
      }
    )

    // Insert 27Q / 144 specific columns before sectionCode
    if (is27Q) {
      cols.push(
        { key: 'natureOfRemittance', label: 'Remittance', type: 'select', width: '200px', options: remittanceOptions || [], required: true },
        { key: 'country', label: 'Country', type: 'select', width: '180px', options: countryOptions || [], required: true },
        { key: 'applicableTds', label: 'Appl. TDS', type: 'select', width: '150px', options: applicableTdsOptions || [], required: true },
        { key: 'optOutTaxation', label: 'Opting Out New Regime', type: 'select', width: '180px', options: [{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }], required: true },
        { key: 'grossedUp393_10', label: 'Grossed Up u/s 393(10)', type: 'select', width: '180px', options: [{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }], required: true },
        { key: 'email', label: 'Email ID', type: 'text', width: '200px' },
        { key: 'contactNo', label: 'Contact No', type: 'text', width: '150px' },
        { key: 'address', label: 'Address', type: 'text', width: '250px' },
        { key: 'tin', label: 'Tax ID / TIN', type: 'text', width: '150px' }
      )
    }

    // Insert 27EQ specific columns
    if (is27EQ) {
      cols.push(
        {
          key: 'residentialStatus',
          label: 'Res. Status',
          type: 'select',
          width: '150px',
          options: [
            { value: 'N', label: 'Resident' },
            { value: 'Y', label: 'Non-Resident' }
          ]
        },
        {
          key: 'hasPE',
          label: 'Has PE in India',
          type: 'select',
          width: '150px',
          options: [
            { value: 'Y', label: 'Yes' },
            { value: 'N', label: 'No' }
          ]
        },
        {
          key: 'optOutRegime',
          label: 'Opting Out New Regime',
          type: 'select',
          width: '180px',
          options: [
            { value: 'Y', label: 'Yes' },
            { value: 'N', label: 'No' }
          ],
          required: true
        }
      )
    }

    cols.push(
      { key: 'sectionCode', label: is27EQ ? 'Collection Code' : 'Section', type: 'select', width: '300px', options: sectionOptions, required: true }
    )

    if (!shouldHideRate) {
      cols.push({ key: 'rateOfTax', label: 'Rate %', type: 'number', width: '80px', required: true })
    }

    cols.push(
      { key: 'tdsAmount', label: is27EQ ? 'TCS Amt' : 'TDS Amt', type: 'number', width: '100px', required: true },
      { key: 'dateOfDeduction', label: is27EQ ? 'Date Coll.' : 'Date Ded.', type: 'date', width: '120px', placeholder: 'DD-MM-YYYY', required: true },
      {
        key: 'challanId',
        label: 'Challan',
        type: 'select',
        width: '250px',
        options: challanData.map((c, i) => ({
          value: c.id,
          label: `${i + 1} (${c.bsrCode || '-'}, ₹${c.totalAmount || c.challanAmount || c.tdsAmount || 0})`
        })),
        required: true,
        readOnly: (row) => isCorrectionMode && !isRowNew(row),
        disabled: (row) => isCorrectionMode && !isRowNew(row)
      },
      { key: 'certificateNo197', label: 'Cert. 197', type: 'text', width: '100px' },
      {
        key: 'reasonForNonDeduction',
        label: 'Reason Non-Ded',
        type: 'select',             // Changed from 'text' to 'select'
        width: '150px',
        options: formattedReasonOptions // Use form-specific options
      }
    )
    return cols
  }

  // Calculate Totals
  const totalTaxable = (tdsData || []).reduce((acc, row) => {
    const val = parseFloat(row.amountOfPayment ?? row.paymentAmount ?? row.amount);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);

  const totalTds = (tdsData || []).reduce((acc, row) => {
    const val = parseFloat(row.tdsAmount ?? row.tds ?? row.tdsAmt ?? row.taxAmount ?? row.totalTds ?? row.tcsAmount ?? row.tcsAmt);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);

  const isFiltered = (searchQuery && searchQuery.trim() !== '') || (isCorrectionMode && filterMode === 'changed');
  const filteredTaxable = (filteredTdsData || []).reduce((acc, row) => {
    const val = parseFloat(row.amountOfPayment ?? row.paymentAmount ?? row.amount);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);

  const filteredTds = (filteredTdsData || []).reduce((acc, row) => {
    const val = parseFloat(row.tdsAmount ?? row.tds ?? row.tdsAmt ?? row.taxAmount ?? row.totalTds ?? row.tcsAmount ?? row.tcsAmt);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);

  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            {is27EQ ? 'TCS / Collectee Details' : 'TDS / Deductee Details'}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Review and manage individual transaction deductions and payments
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isCorrectionMode && (
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setFilterMode('all')}
                className={`px-3 py-1.5 rounded-md transition-colors ${filterMode === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                All Records ({tdsData.length})
              </button>
              <button
                type="button"
                onClick={() => setFilterMode('changed')}
                className={`px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${filterMode === 'changed' ? 'bg-amber-500 text-white shadow-sm' : 'text-amber-700 hover:bg-amber-50'}`}
              >
                <span className="w-2 h-2 rounded-full bg-amber-300"></span>
                Changed Records ({changedCount})
              </button>
            </div>
          )}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by PAN or Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-60 shadow-sm text-sm"
            />
          </div>
          <button
            onClick={handleGridRowAdd}
            className="flex items-center px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md border-2 border-green-500 font-bold text-sm"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Row
          </button>
        </div>
      </div>

      {/* Summary KPI Cards for Taxable Value and TDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {/* Total Taxable Value */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50/60 border border-blue-200/80 rounded-xl p-3.5 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">
              {is27EQ ? 'Total Value of Purchase' : 'Total Taxable Value'}
            </span>
            <span className="p-1 bg-blue-100 text-blue-700 rounded-md text-xs font-bold flex items-center justify-center">
              <IndianRupee className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="mt-2">
            <div className="text-xl font-bold text-blue-900 tracking-tight">
              ₹{formatCurrency(totalTaxable)}
            </div>
            {isFiltered && (
              <div className="text-xs text-blue-600 mt-0.5 font-medium">
                Filtered: ₹{formatCurrency(filteredTaxable)}
              </div>
            )}
            <div className="text-[11px] text-gray-500 mt-0.5">
              Across {tdsData.length} {is27EQ ? 'collectee' : 'deductee'} record{tdsData.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>

        {/* Total TDS / TCS */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50/60 border border-emerald-200/80 rounded-xl p-3.5 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              {is27EQ ? 'Total TCS Amount' : 'Total TDS Amount'}
            </span>
            <span className="p-1 bg-emerald-100 text-emerald-700 rounded-md text-xs font-bold flex items-center justify-center">
              <IndianRupee className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="mt-2">
            <div className="text-xl font-bold text-emerald-900 tracking-tight">
              ₹{formatCurrency(totalTds)}
            </div>
            {isFiltered && (
              <div className="text-xs text-emerald-600 mt-0.5 font-medium">
                Filtered: ₹{formatCurrency(filteredTds)}
              </div>
            )}
            <div className="text-[11px] text-gray-500 mt-0.5">
              Total tax deducted / collected
            </div>
          </div>
        </div>

        {/* Total Records */}
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 border border-gray-200 rounded-xl p-3.5 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
              {is27EQ ? 'Total Collectees' : 'Total Deductees'}
            </span>
            <span className="p-1 bg-gray-200 text-gray-700 rounded-md text-xs font-bold flex items-center justify-center">
              <Hash className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="mt-2">
            <div className="text-xl font-bold text-gray-900 tracking-tight">
              {tdsData.length}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {isFiltered ? `Showing ${filteredTdsData.length} matching rows` : 'Total records registered'}
            </div>
          </div>
        </div>

        {/* Status / Context Card */}
        {isCorrectionMode ? (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50/60 border border-amber-200/80 rounded-xl p-3.5 shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                Correction Status
              </span>
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
            </div>
            <div className="mt-2">
              <div className="text-xl font-bold text-amber-900 tracking-tight">
                {changedCount} Changed
              </div>
              <div className="text-[11px] text-amber-700 mt-0.5">
                {tdsData.length - changedCount} Unchanged records
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-purple-50 to-violet-50/60 border border-purple-200/80 rounded-xl p-3.5 shadow-sm flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-purple-700">
                Linked Challans
              </span>
              <span className="p-1 bg-purple-100 text-purple-700 rounded-md text-xs font-bold flex items-center justify-center">
                <Layers className="w-3.5 h-3.5" />
              </span>
            </div>
            <div className="mt-2">
              <div className="text-xl font-bold text-purple-900 tracking-tight">
                {challanData.length} Challan{challanData.length === 1 ? '' : 's'}
              </div>
              <div className="text-[11px] text-purple-700 mt-0.5">
                ₹{formatCurrency(challanData.reduce((s, c) => s + (parseFloat(c.totalAmount || c.challanAmount || 0) || 0), 0))} deposited
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <ExcelDataGrid
          columns={getColumns()}
          data={filteredTdsData}
          onDataChange={handleGridDataChange}
          onRowAdd={handleGridRowAdd}
          onRowDelete={handleGridRowDelete}
          height="calc(100vh - 460px)"
          stickyFirstColumn={false}
          getRowClassName={getRowClassName}
          isRowDeleteDisabled={isRowDeleteDisabled}
          onRowRevert={isRevised ? handleRowRevert : undefined}
          summaryRow={filteredTdsData.length > 0 ? {
            deducteeCode: <span className="font-bold text-gray-800">TOTAL:</span>,
            panOfDeductee: !(is27EQ || is27Q || is26Q) ? <span className="font-bold text-gray-800">TOTAL:</span> : undefined,
            amountOfPayment: <span className="font-bold text-blue-700">₹{formatCurrency(filteredTaxable)}</span>,
            tdsAmount: <span className="font-bold text-emerald-700">₹{formatCurrency(filteredTds)}</span>
          } : null}
        />

        {tdsData.length === 0 && (
          <div className="p-4 text-center text-gray-500 text-sm">
            No TDS details added yet. Use the grid above or "Upload Excel" to add data.
          </div>
        )}
      </div>


    </div>
  )
}

export default TdsDetailsTable
